// hooks/useRecurringExpenses.jsx
import { useState, useEffect, useCallback } from 'react';
import { getRecurringExpensesDue } from '../Fetch/fetchExpenses';

export const useRecurringExpenses = (buildingId, isEnabled = true) => {
    const [recurringExpenses, setRecurringExpenses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [hasCheckedToday, setHasCheckedToday] = useState(false);
    const [hasCheckedThisSession, setHasCheckedThisSession] = useState(false);

    // Check if we've already shown popup this session
    const checkIfShownThisSession = useCallback(() => {
        const shownThisSession = sessionStorage.getItem(`recurringExpensesShown_${buildingId}`);
        return shownThisSession === 'true';
    }, [buildingId]);

    // Mark as shown for this session
    const markAsShownThisSession = useCallback(() => {
        sessionStorage.setItem(`recurringExpensesShown_${buildingId}`, 'true');
        setHasCheckedThisSession(true);
    }, [buildingId]);

    // Check if we've already checked this month (to avoid showing popup multiple times per month)
    const checkIfAlreadyCheckedThisMonth = useCallback(() => {
        const lastCheck = localStorage.getItem(`recurringExpensesCheck_${buildingId}`);
        const currentMonth = new Date().getMonth() + '_' + new Date().getFullYear();
        
        if (lastCheck === currentMonth) {
            setHasCheckedToday(true);
            return true;
        }
        return false;
    }, [buildingId]);

    // Mark as checked for this month
    const markAsCheckedThisMonth = useCallback(() => {
        const currentMonth = new Date().getMonth() + '_' + new Date().getFullYear();
        localStorage.setItem(`recurringExpensesCheck_${buildingId}`, currentMonth);
        setHasCheckedToday(true);
    }, [buildingId]);

    // Reset check (for testing or manual trigger)
    const resetMonthlyCheck = useCallback(() => {
        localStorage.removeItem(`recurringExpensesCheck_${buildingId}`);
        setHasCheckedToday(false);
    }, [buildingId]);

    const fetchRecurringExpenses = useCallback(async (signal, forceShow = false) => {
        if (!buildingId) {
            return;
        }

        if (!isEnabled && !forceShow) {
            return;
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
            const expenses = await getRecurringExpensesDue(buildingId, signal);
            
            // Check if request was aborted
            if (signal?.aborted) {
                return;
            }
            setRecurringExpenses(expenses || []);
            
            // Show popup if there are expenses and haven't shown this session (or forced)
            if (expenses && expenses.length > 0) {
                if (forceShow) {
                    setShowPopup(true);
                } else {
                    const shownThisSession = checkIfShownThisSession();
                    if (!shownThisSession) {
                        setShowPopup(true);
                        markAsShownThisSession();
                    }
                }
            } else {
                // Only mark as checked when there are NO expenses to create
                if (expenses && expenses.length === 0) {
                    markAsCheckedThisMonth();
                }
            }
        } catch (err) {
            // Don't update state if request was aborted
            if (signal?.aborted || err.name === 'AbortError') {
                return;
            }
            
            console.error('Error fetching recurring expenses:', err);
            setError(err.message);
        } finally {
            // Don't update state if request was aborted
            if (!signal?.aborted) {
                setIsLoading(false);
            }
        }
    }, [buildingId, isEnabled, hasCheckedToday, markAsCheckedThisMonth, checkIfShownThisSession, markAsShownThisSession]);

    // Manual refresh function
    const refreshRecurringExpenses = useCallback(async (force = false) => {
        if (force) {
            resetMonthlyCheck();
        }
        const abortController = new AbortController();
        await fetchRecurringExpenses(abortController.signal);
    }, [fetchRecurringExpenses, resetMonthlyCheck]);

    // Close popup handler
    const closePopup = useCallback(() => {
        setShowPopup(false);
    }, []);

    // Handler for when expenses are created
    const handleExpensesCreated = useCallback((createdExpenses) => {
        if (createdExpenses && createdExpenses.length > 0) {
            // Remove created/stopped expenses from the list
            setRecurringExpenses(prev => {
                const newExpenses = prev.filter(expense => 
                    !createdExpenses.some(created => {
                        // Handle both created expenses and stopped payments
                        if (created.stopped) {
                            return created.ExpenseID === (expense.expenseID || expense.ExpenseID);
                        }
                        return created.Description === (expense.description || expense.Description) &&
                               created.PaidTo === (expense.paidTo || expense.PaidTo) &&
                               created.Amount === (expense.amount || expense.Amount);
                    })
                );
                
                // If all expenses have been handled, mark as checked for this month
                if (newExpenses.length === 0) {
                    markAsCheckedThisMonth();
                    setShowPopup(false);
                }
                
                return newExpenses;
            });
        }
    }, [markAsCheckedThisMonth]);

    // Show popup manually (for testing or manual trigger)
    const showPopupManually = useCallback(async () => {
        const abortController = new AbortController();
        
        // Always fetch fresh data and force show popup
        await fetchRecurringExpenses(abortController.signal, true);
    }, [fetchRecurringExpenses]);

    // Initial load effect - always check for expenses on load
    useEffect(() => {
        if (buildingId && isEnabled) {
            // Create AbortController for cleanup
            const abortController = new AbortController();
            
            // Delay the check slightly to allow dashboard to load
            const timer = setTimeout(() => {
                fetchRecurringExpenses(abortController.signal);
            }, 3000); // Increased delay to 3 seconds
            
            // Cleanup function to clear timeout and abort request
            return () => {
                clearTimeout(timer);
                abortController.abort();
            };
        }
        
        // No cleanup needed
        return undefined;
    }, [buildingId, isEnabled, fetchRecurringExpenses]);


    return {
        recurringExpenses,
        isLoading,
        error,
        showPopup,
        hasCheckedToday,
        closePopup,
        refreshRecurringExpenses,
        handleExpensesCreated,
        resetMonthlyCheck, // For debugging/testing
        fetchRecurringExpenses, // Manual trigger
        showPopupManually // For manual popup display
    };
};